/**
 * Utility functions for error handling and message extraction
 */

/**
 * Extract a human-readable error message from various error types
 * @param error - The error object, string, or unknown value
 * @returns A string error message suitable for display to users
 */
export const getErrorMessage = (error: unknown): string => {
  // Handle string errors
  if (typeof error === 'string') {
    return error
  }

  // Handle Error instances
  if (error instanceof Error) {
    return error.message
  }

  // Handle API error objects and other object types
  if (error && typeof error === 'object') {
    const apiError = error as any

    // Try different common error message properties
    if (apiError.message && typeof apiError.message === 'string') {
      return apiError.message
    }

    if (apiError.error && typeof apiError.error === 'string') {
      return apiError.error
    }

    if (
      apiError.details &&
      apiError.details.message &&
      typeof apiError.details.message === 'string'
    ) {
      return apiError.details.message
    }

    if (
      apiError.details &&
      apiError.details.error &&
      typeof apiError.details.error === 'string'
    ) {
      return apiError.details.error
    }

    // Handle response objects with data
    if (
      apiError.data &&
      apiError.data.message &&
      typeof apiError.data.message === 'string'
    ) {
      return apiError.data.message
    }

    if (
      apiError.data &&
      apiError.data.error &&
      typeof apiError.data.error === 'string'
    ) {
      return apiError.data.error
    }
  }

  // Fallback for unknown error types
  return 'Ha ocurrido un error inesperado'
}

/**
 * Check if an error indicates a network or connectivity issue
 * @param error - The error to check
 * @returns True if the error appears to be network-related
 */
export const isNetworkError = (error: unknown): boolean => {
  if (typeof error === 'string') {
    const lowerError = error.toLowerCase()
    return (
      lowerError.includes('network') ||
      lowerError.includes('timeout') ||
      lowerError.includes('connection')
    )
  }

  if (error instanceof Error) {
    const lowerMessage = error.message.toLowerCase()
    return (
      lowerMessage.includes('network') ||
      lowerMessage.includes('timeout') ||
      lowerMessage.includes('connection') ||
      error.name === 'NetworkError' ||
      error.name === 'TimeoutError'
    )
  }

  if (error && typeof error === 'object') {
    const apiError = error as any

    // Check status codes
    if (typeof apiError.status === 'number') {
      if (apiError.status === 0 || apiError.status >= 500) {
        return true
      }
    }

    // Check error codes
    if (apiError.code && typeof apiError.code === 'string') {
      const networkCodes = ['NETWORK_ERROR', 'TIMEOUT', 'CONNECTION_ERROR']
      if (networkCodes.includes(apiError.code)) {
        return true
      }
    }
  }

  return false
}

/**
 * Get a user-friendly error message with context
 * @param error - The error object
 * @param context - Additional context about what operation failed
 * @returns A formatted error message
 */
export const getContextualErrorMessage = (
  error: unknown,
  context: string
): string => {
  const baseMessage = getErrorMessage(error)

  if (isNetworkError(error)) {
    return `${context}: Problema de conexión. Por favor, verifica tu conexión a internet e intenta nuevamente.`
  }

  return `${context}: ${baseMessage}`
}

/**
 * Log error details for debugging while returning a user-friendly message
 * @param error - The error to log and process
 * @param context - Context about where the error occurred
 * @returns A user-friendly error message
 */
export const logAndGetErrorMessage = (
  error: unknown,
  context: string
): string => {
  // Log the full error for debugging
  console.error(`Error in ${context}:`, error)

  // Return user-friendly message
  return getContextualErrorMessage(error, context)
}
